import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useRouteError } from "react-router-dom";
import { AlertCircle, ArrowLeft, Home, RefreshCw, Clipboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/shared/EmptyState";
import { resolveRoutePolicy } from "@/lib/route-policies";

const buildDiagnostics = (error: unknown, route: string) => {
  const normalized = error instanceof Error ? error : new Error(String(error));
  return [
    "ForgeHQ route error report",
    `Route: ${route}`,
    `Time: ${new Date().toISOString()}`,
    `Message: ${normalized.message}`,
    normalized.stack,
  ]
    .filter(Boolean)
    .join("\n");
};

export function RouteErrorFallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const routePolicy = useMemo(() => resolveRoutePolicy(location.pathname), [location.pathname]);
  const route = location.pathname + location.search + location.hash;
  const routeError = useRouteError();
  const [copied, setCopied] = useState(false);
  const error = routeError instanceof Error ? routeError : new Error(String(routeError));
  const diagnostics = useMemo(() => buildDiagnostics(error, route), [error, route]);

  const handleRetry = useCallback(() => {
    window.location.reload();
  }, []);

  const handleGoHome = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const handleGoBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/");
  }, [navigate]);

  const handleCopyDiagnostics = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(diagnostics);
      setCopied(true);
    } catch (clipboardError) {
      console.error("Failed to copy diagnostics", clipboardError);
      setCopied(false);
    }
  }, [diagnostics]);

  return (
    <EmptyState
      title="Something interrupted this page"
      description={
        routePolicy.routeRecoveryHint ??
        "The page could not render successfully. Retry, return home, or copy diagnostics to share with support."
      }
      icon={AlertCircle}
      variant="empty"
      primaryAction={
        <div className="flex flex-wrap justify-center gap-2">
          <Button
            variant="primary"
            onClick={handleRetry}
            className="gap-2"
            aria-label="Retry loading page"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
          <Button variant="outline" onClick={handleGoHome} className="gap-2" aria-label="Go to home route">
            <Home className="h-4 w-4" />
            Home
          </Button>
          <Button variant="secondary" onClick={handleGoBack} className="gap-2" aria-label="Go to previous route">
            <ArrowLeft className="h-4 w-4" />
            Go back
          </Button>
          <Button variant="ghost" onClick={handleCopyDiagnostics} className="gap-2" aria-label="Copy diagnostics">
            <Clipboard className="h-4 w-4" />
            {copied ? "Copied" : "Copy diagnostics"}
          </Button>
        </div>
      }
      className="max-w-2xl"
      children={
        <details className="mt-2 w-full max-w-lg text-left">
          <summary className="cursor-pointer text-sm text-muted-foreground">Technical details</summary>
          <pre className="mt-2 max-h-56 overflow-auto rounded-lg bg-muted/70 p-3 text-xs text-muted-foreground">
            {diagnostics}
          </pre>
        </details>
      }
    />
  );
}
